// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Organization } from '@/account/api/types';
import {
  CheckCircle,
  ErrorOutlineOutlined,
  HighlightOff,
  Settings,
  SyncOutlined,
} from '@/constants/hardfin-standard-icons';
import { SalesforceLogo } from '@/icons/salesforce-logo';
import { findIntegrationConfig } from '@/utilities/integration';
import {
  Box,
  Button,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

interface SalesforceCRMRowProps {
  organization: Organization;
  isHubSpotConnected: boolean;
  isEnabledForOrg: boolean;
  onOpenSalesforceDialog: () => void;
}

interface SettingsButtonProps {
  onOpen: () => void;
  buttonLabel: string;
  text: string;
}

function SettingsButton(props: SettingsButtonProps) {
  return (
    <Typography sx={{ display: 'flex', alignItems: 'center' }}>
      <Button
        startIcon={<Settings color={'action'} fontSize={'small'} />}
        onClick={props.onOpen}
        variant={'outlined'}
      >
        {props.buttonLabel}
      </Button>
      <Typography sx={{ ml: 1 }}>{props.text}</Typography>
    </Typography>
  );
}

export function SalesforceCRMRow(props: SalesforceCRMRowProps) {
  const crmConfig = findIntegrationConfig(
    props.organization.integrationConfigurations,
    'SFDC'
  );
  const isSalesforceConfiguration = crmConfig !== null;

  // Type guard to properly discriminate the Salesforce configuration
  const salesforceConfig = isSalesforceConfiguration
    ? crmConfig.configurationState
    : null;

  const isSalesforceConnected = salesforceConfig?.connected ?? false;
  const salesforceAllScopesPresent =
    salesforceConfig?.allScopesPresent ?? false;

  const isDisabledByOtherCRM =
    props.isHubSpotConnected && !isSalesforceConfiguration;
  const isUnavailable = !props.isEnabledForOrg;

  const DisabledCell = (cellProps: { text: string }) => (
    <TableCell>
      <Typography
        sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled' }}
      >
        <HighlightOff color={'disabled'} fontSize={'small'} sx={{ mr: 1 }} />
        {cellProps.text}
      </Typography>
    </TableCell>
  );

  if (isUnavailable) {
    return (
      <TableRow sx={{ opacity: 0.8 }}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Contact support to enable this integration" arrow>
              <SalesforceLogo sx={{ width: 100, height: 32, opacity: 0.5 }} />
            </Tooltip>
          </Box>
        </TableCell>
        <DisabledCell text="Salesforce integration is not enabled for your organization, contact Hardfin support to activate" />
        <DisabledCell text="Salesforce integration is not enabled for your organization, contact Hardfin support to activate" />
        <DisabledCell text="Salesforce integration is not enabled for your organization, contact Hardfin support to activate" />
        <TableCell align={'right'}>{/* Unavailable - no settings */}</TableCell>
      </TableRow>
    );
  }

  if (isDisabledByOtherCRM) {
    return (
      <TableRow sx={{ opacity: 0.8 }}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Only one CRM can be connected at a time" arrow>
              <SalesforceLogo sx={{ width: 100, height: 32, opacity: 0.5 }} />
            </Tooltip>
          </Box>
        </TableCell>
        <DisabledCell text="Not available while HubSpot is already connected" />
        <DisabledCell text="Not available while HubSpot is already connected" />
        <DisabledCell text="Not available while HubSpot is already connected" />
        <TableCell align={'right'}>{/* Disabled - no settings */}</TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SalesforceLogo sx={{ width: 100, height: 32 }} />
        </Box>
      </TableCell>
      <TableCell>
        {!isSalesforceConfiguration ? (
          <SettingsButton
            onOpen={props.onOpenSalesforceDialog}
            buttonLabel="Connect"
            text={'Not connected'}
          />
        ) : !isSalesforceConnected ? (
          <SettingsButton
            onOpen={props.onOpenSalesforceDialog}
            buttonLabel="Connect"
            text={'Not connected'}
          />
        ) : salesforceAllScopesPresent ? (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircle color={'success'} fontSize={'small'} sx={{ mr: 1 }} />{' '}
            Connected
          </Typography>
        ) : (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              startIcon={
                <ErrorOutlineOutlined
                  color={'warning'}
                  fontSize={'small'}
                  sx={{ mr: 1 }}
                />
              }
              onClick={props.onOpenSalesforceDialog}
              variant={'outlined'}
            >
              Partially connected
            </Button>
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {isSalesforceConnected && salesforceAllScopesPresent ? (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            Managed by Hardfin
          </Typography>
        ) : (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <HighlightOff color={'action'} fontSize={'small'} sx={{ mr: 1 }} />
            Not configured
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {isSalesforceConnected && salesforceAllScopesPresent ? (
          salesforceConfig?.lastSyncedAt !== null &&
          salesforceConfig?.lastSyncedAt !== undefined ? (
            <Typography sx={{ display: 'flex', alignItems: 'center' }}>
              <SyncOutlined
                color={'success'}
                fontSize={'small'}
                sx={{ mr: 1 }}
              />
              Last sync at{' '}
              {salesforceConfig.lastSyncedAt
                .setZone(props.organization.timezone)
                .toLocaleString({
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
            </Typography>
          ) : (
            <Typography sx={{ display: 'flex', alignItems: 'center' }}>
              <HighlightOff
                color={'action'}
                fontSize={'small'}
                sx={{ mr: 1 }}
              />
              Not syncing
            </Typography>
          )
        ) : (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <HighlightOff color={'action'} fontSize={'small'} sx={{ mr: 1 }} />
            Not syncing
          </Typography>
        )}
      </TableCell>
      <TableCell align={'right'}>
        {/* Configuration managed by Hardfin */}
      </TableCell>
    </TableRow>
  );
}
